import { useMemo, useState, useEffect, useCallback } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import PropTypes from "prop-types";

import DeleteConfirmModal from "./DeleteConfirmModal";
import api from "../../utilities/api";

const ROLES = ["admin", "member", "user"];

// Editable role cell
const RoleCell = ({ getValue, row, onRoleChange }) => {
  const [value, setValue] = useState(getValue());

  useEffect(() => {
    setValue(getValue());
  }, [getValue]);

  const handleChange = (e) => {
    const newRole = e.target.value;
    setValue(newRole);
    onRoleChange(row.original._id, newRole);
  };

  return (
    <select
      value={value}
      onChange={handleChange}
      className="bg-transparent border border-gray-500 rounded px-2 py-1 text-sm focus:outline-none focus:border-cyan-600"
    >
      {ROLES.map((role) => (
        <option key={role} value={role}>
          {role}
        </option>
      ))}
    </select>
  );
};

RoleCell.propTypes = {
  getValue: PropTypes.func.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({ _id: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
  onRoleChange: PropTypes.func.isRequired,
};

const columnHelper = createColumnHelper();

const UserTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null,
    userName: "",
  });

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Update user role
  const handleRoleChange = useCallback(
    async (userId, newRole) => {
      try {
        await api.patch(`/users/${userId}/role`, {
          role: newRole,
        });
        setData((prev) =>
          prev.map((user) =>
            user._id === userId ? { ...user, role: newRole } : user,
          ),
        );
        toast.success("Role updated");
      } catch (error) {
        console.error("Error updating role:", error);
        toast.error(error.response?.data?.message || "Failed to update role");
        // Revert by refetching
        fetchUsers();
      }
    },
    [fetchUsers],
  );

  // Delete user
  const handleDelete = useCallback(async () => {
    const { userId } = deleteModal;
    if (!userId) return;

    try {
      await api.delete(`/users/${userId}`);
      setData((prev) => prev.filter((user) => user._id !== userId));
      toast.success("User deleted");
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.message || "Failed to delete user");
    } finally {
      setDeleteModal({ isOpen: false, userId: null, userName: "" });
    }
  }, [deleteModal]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: (info) => <span>{info.getValue()}</span>,
        size: 200,
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: (info) => <span>{info.getValue()}</span>,
        size: 280,
      }),
      columnHelper.accessor("isVerified", {
        header: "Verified",
        cell: (info) => (
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
              info.getValue()
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {info.getValue() ? "Yes" : "No"}
          </span>
        ),
        size: 100,
      }),
      columnHelper.accessor("lastLogin", {
        header: "Last Login",
        cell: (info) => (
          <span className="text-sm">{formatDate(info.getValue())}</span>
        ),
        size: 180,
      }),
      columnHelper.accessor("role", {
        header: "Role",
        cell: (info) => (
          <RoleCell
            getValue={info.getValue}
            row={info.row}
            onRoleChange={handleRoleChange}
          />
        ),
        size: 130,
      }),
      columnHelper.display({
        id: "delete",
        header: "",
        cell: ({ row }) => (
          <button
            onClick={() =>
              setDeleteModal({
                isOpen: true,
                userId: row.original._id,
                userName: row.original.name,
              })
            }
            className="py-0 px-2 text-center text-red-700 font-normal text-sm border-2 border-red-300 bg-white hover:bg-red-50 hover:border-red-500 rounded-lg transition duration-300"
          >
            Delete
          </button>
        ),
        size: 80,
      }),
    ],
    [handleRoleChange],
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 10 },
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Loading users...
      </div>
    );
  }

  return (
    <div className="p-2 text-black fill-gray-600">
      <div className="mt-2 flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle">
            <table className="min-w-full text-left">
              <thead className="bg-cyan-900 text-white">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={
                          header.column.columnDef.size
                            ? { width: header.column.columnDef.size }
                            : {}
                        }
                        className={`py-2 px-2 ${
                          header.id === "lastLogin"
                            ? "hidden md:table-cell"
                            : ""
                        } ${
                          header.id === "isVerified"
                            ? "hidden sm:table-cell"
                            : ""
                        }`}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getCanSort() && (
                          <button
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <ArrowsUpDownIcon
                              className="h-6 w-6 text-white relative"
                              style={{ top: "6px" }}
                            />
                          </button>
                        )}
                        {
                          {
                            asc: "🔼",
                            desc: "🔽",
                          }[header.column.getIsSorted()]
                        }
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={i % 2 === 0 ? "bg-slate-100" : "bg-slate-300"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`py-1 px-2 ${
                            cell.column.id === "lastLogin"
                              ? "hidden md:table-cell"
                              : ""
                          } ${
                            cell.column.id === "isVerified"
                              ? "hidden sm:table-cell"
                              : ""
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr className="text-center h-32">
                    <td colSpan={columns.length}>No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end mt-2 gap-2">
        <button
          onClick={() => table.firstPage()}
          disabled={!table.getCanPreviousPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<<"}
        </button>
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<"}
        </button>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">"}
        </button>
        <button
          onClick={() => table.lastPage()}
          disabled={!table.getCanNextPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">>"}
        </button>
        <span className="flex items-center gap-1">
          <input
            type="number"
            value={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              table.setPageIndex(page);
            }}
            className="border text-right rounded w-14 bg-transparent"
          />{" "}
          of {table.getPageCount()}
        </span>
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          className="p-2 bg-transparent"
        >
          {[10, 25, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, userId: null, userName: "" })
        }
        onConfirm={handleDelete}
        title="Delete User"
        message={`Are you sure you want to delete "${deleteModal.userName}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default UserTable;
